import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  BRANDING_COLOR_FIELDS,
  DEFAULT_BRANDING_COLORS,
  applyBranding,
  isValidHex,
  normalizeHexClient,
  type BrandingColors,
} from '@/lib/branding';

interface Props {
  initialColors: BrandingColors;
  onSaved?: () => void;
}

export default function BrandingColorsSettings({ initialColors, onSaved }: Props) {
  const [colors, setColors] = useState<BrandingColors>(initialColors);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setColors(initialColors);
  }, [initialColors]);

  const setColor = (key: keyof BrandingColors, value: string) => {
    const next = { ...colors, [key]: value };
    setColors(next);
    // Aperçu en direct sur toute la plateforme
    if (BRANDING_COLOR_FIELDS.every((f) => isValidHex(next[f.key]))) {
      applyBranding(next);
    }
  };

  const resetPalette = () => {
    setColors(DEFAULT_BRANDING_COLORS);
    applyBranding(DEFAULT_BRANDING_COLORS);
  };

  const save = async () => {
    const invalid = BRANDING_COLOR_FIELDS.find((f) => !isValidHex(colors[f.key]));
    if (invalid) {
      setError(`Couleur invalide pour « ${invalid.label} »`);
      setTimeout(() => setError(null), 4000);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const normalized = Object.fromEntries(
        BRANDING_COLOR_FIELDS.map((f) => [f.key, normalizeHexClient(colors[f.key])]),
      ) as unknown as BrandingColors;

      const { data: row } = await supabase
        .from('zifek')
        .select('id')
        .limit(1)
        .maybeSingle();

      if (row?.id) {
        const { error: upErr } = await supabase
          .from('zifek')
          .update({ branding: normalized })
          .eq('id', row.id);
        if (upErr) throw upErr;
      } else {
        const { error: insErr } = await supabase
          .from('zifek')
          .insert({ branding: normalized });
        if (insErr) throw insErr;
      }

      setColors(normalized);
      applyBranding(normalized);
      setSaved(true);
      onSaved?.();
      setTimeout(() => setSaved(false), 2200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setTimeout(() => setError(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
      <p className="text-xs text-foreground-500 mb-4">
        Les couleurs du logo Zifek pilotent toute la plateforme. Modifiez-les ici : le changement
        s&apos;applique partout (site vitrine, dashboard, superadmin).
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {BRANDING_COLOR_FIELDS.map((field) => (
          <div key={field.key} className="flex items-center gap-3">
            <input
              type="color"
              value={isValidHex(colors[field.key]) ? normalizeHexClient(colors[field.key]) : '#000000'}
              onChange={(e) => setColor(field.key, e.target.value)}
              className="w-11 h-11 rounded-md border border-background-200/70 bg-background-50 cursor-pointer flex-shrink-0"
              aria-label={field.label}
            />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-foreground-800 truncate">{field.label}</div>
              <input
                type="text"
                value={colors[field.key]}
                onChange={(e) => setColor(field.key, e.target.value)}
                className="mt-0.5 w-24 h-7 px-2 rounded border border-background-200/70 bg-background-50 text-[11px] font-mono text-foreground-700 focus:outline-none focus:border-primary-300"
              />
              <div className="text-[10px] text-foreground-400 truncate">{field.hint}</div>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <p className="mt-4 text-xs text-red-600 bg-red-50/70 px-3 py-2 rounded-md flex items-center gap-1.5">
          <i className="ri-error-warning-line"></i>
          {error}
        </p>
      )}

      <div className="mt-5 flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="h-10 px-5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving ? (
            <><i className="ri-loader-4-line animate-spin text-xs"></i>Enregistrement...</>
          ) : saved ? (
            <><i className="ri-check-line text-xs"></i>Enregistré</>
          ) : (
            <><i className="ri-save-line text-xs"></i>Enregistrer la palette</>
          )}
        </button>
        <button
          onClick={resetPalette}
          disabled={saving}
          className="h-10 px-4 rounded-full border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          Réinitialiser la palette Zifek
        </button>
      </div>
    </div>
  );
}